import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Receipt, Crown, Clock } from "@phosphor-icons/react";
import { AppHeader } from "@/components/AppHeader";
import { InviteCard } from "@/components/InviteCard";
import { ReceiptDialog } from "@/components/ReceiptDialog";
import { myReceiptsApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, shortDate } from "@/lib/format";

function PlanCard({ user }) {
  const navigate = useNavigate();
  const paid = user.plan_status === "active";
  const label = paid ? `${user.plan === "team" ? "Team" : "Pro"}${user.plan_interval === "reward" ? " · free invite reward" : user.plan_interval === "yearly" ? " · Yearly" : " · Monthly"}` : user.access ? "Free trial" : "Trial ended";
  return (
    <div className="stp-card flex flex-col justify-between gap-4 rounded-3xl p-6 sm:flex-row sm:items-center sm:p-8" data-testid="billing-plan-card">
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-soft text-brand">{paid ? <Crown size={20} weight="fill" /> : <Clock size={20} weight="bold" />}</span>
        <div>
          <p data-testid="billing-plan-label" className="font-display text-xl font-semibold text-ink">{label}</p>
          <p className="text-sm text-mist">{paid ? `Active until ${shortDate(user.plan_expires_at)}` : user.access ? `${user.trial_days_left} days left in your trial` : "Upgrade to keep creating prompts"}</p>
        </div>
      </div>
      <button data-testid="billing-change-plan-button" onClick={() => navigate("/pricing")} className="h-11 rounded-full bg-ink px-5 text-sm font-semibold text-white hover:bg-black">{paid ? "Change or renew plan" : "See plans"}</button>
    </div>
  );
}

export default function BillingPage() {
  const { user } = useAuth();
  const [receipts, setReceipts] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => { myReceiptsApi().then(setReceipts).catch(() => {}); }, []);

  return (
    <div className="stp-bg">
      <AppHeader />
      <main className="mx-auto w-full max-w-3xl space-y-6 px-5 pb-28 pt-10 sm:px-8">
        <h1 className="font-display text-4xl font-semibold text-ink sm:text-5xl">Billing</h1>
        <PlanCard user={user} />
        <InviteCard />
        <div className="stp-card rounded-3xl p-6 sm:p-8" data-testid="receipts-card">
          <h2 className="flex items-center gap-2 font-display text-2xl font-semibold text-ink"><Receipt size={22} weight="fill" className="text-brand" /> Receipts</h2>
          {receipts.length === 0 ? (
            <p className="mt-4 text-sm text-mist" data-testid="receipts-empty">No payments yet. Receipts appear here and in your inbox after each payment.</p>
          ) : (
            <ul className="mt-4 divide-y divide-black/5">
              {receipts.map((r) => (
                <li key={r.id}>
                  <button data-testid="receipt-row" onClick={() => setOpen(r)} className="flex w-full items-center justify-between gap-3 py-3.5 text-left hover:opacity-80">
                    <span>
                      <span className="block text-sm font-medium text-ink">{r.plan_label} · {r.interval === "yearly" ? "Yearly" : "Monthly"}</span>
                      <span className="block text-xs text-mist">{shortDate(r.created_at)} · {r.number}</span>
                    </span>
                    <span className="text-right">
                      <span className="block text-sm font-semibold text-ink">{money(r.amount, r.currency)}</span>
                      {r.sale_code && <span className="block text-[11px] font-medium text-[#C93400]">{r.sale_code} applied</span>}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
      <ReceiptDialog receipt={open} open={!!open} onOpenChange={(v) => !v && setOpen(null)} />
    </div>
  );
}
